import { useState, useEffect, useCallback } from 'react';
import { Box, Grid2, ToggleButton, ToggleButtonGroup, useMediaQuery, useTheme, Tooltip, IconButton, Typography, Collapse, Button } from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import DownloadIcon from '@mui/icons-material/Download';
import LoanCard from './LoanCard';
import LoanTable from './LoanTable';
import { useAuth } from '../../../../../services/auth/AuthContext';
import FilterBar from './FilterBar';
import apiRequest from '../../../../../services/api/handler';
import { useNavigate } from 'react-router-dom';
import OverlayLoader from '../../../../../components/overlayLoader';
import InfiniteScroll from 'react-infinite-scroll-component';
import CircularProgress from '@mui/material/CircularProgress';
import Zoom from '@mui/material/Zoom';
import Fab from '@mui/material/Fab';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { resellerStatusPerLoanType } from '../utils';

export default function LoanCardList({
    applications,
    setApplications,
    allStatus,
    currentStatusCode,
    resellerData,
    loanType,
    loadMore,
    hasMore,
    showAllCloudDownload = false,
    displayRejectionReasonFilter = false,
    showStepper = true,
}) {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
    const navigate = useNavigate();

    const { setIsAuthenticated, user } = useAuth();
    const userRoles = user.role;
    const isSupport = userRoles.includes('support');
    const isConnector = userRoles.includes('connector');
    const isPartner = userRoles.includes('partner');

    const [loading, setLoading] = useState(false);
    const [currentView, setCurrentView] = useState('grid');
    const [showScrollTop, setShowScrollTop] = useState(false);

    const handleViewChange = useCallback((e, value) => {
        if (value) {
            setCurrentView(value);
        }
    }, []);

    const isInitialLoad = applications.length === 0;

    const handleDownloadReport = useCallback((reportType) => {
        setLoading(true);
        const reportTypes = {
            enquiryDetails: {
                url: `/reseller/detailed-enquiry-data?loan_type=${loanType}`,
                fileName: `detailed_enquiry_data_${loanType}.csv`,
            },
            allCloudLeads: {
                url: `/reseller/allcloud-leads-data?loan_type=${loanType}`,
                fileName: `allcloud_leads_data_${loanType}.csv`,
            },
        };
        apiRequest(
            reportTypes[reportType].url,
            'GET',
            null,
            true,
            setIsAuthenticated,
            navigate,
            true
        )
            .then((response) => {
                const blob = new Blob([response.data], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', reportTypes[reportType].fileName);
                document.body.appendChild(link);
                link.click();

                link.parentNode.removeChild(link);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error downloading report:', error);
                setLoading(false);
            });
    }, [loanType, setIsAuthenticated, navigate]);

    useEffect(() => {
        const scrollContainer = document.getElementById('scrollableDiv') || window;

        const handleScroll = () => {
            const scrollPosition = scrollContainer === window
                ? window.pageYOffset
                : scrollContainer.scrollTop;

            setShowScrollTop(scrollPosition > 300);
        };

        scrollContainer.addEventListener('scroll', handleScroll);
        return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = useCallback(() => {
        const scrollContainer = document.getElementById('scrollableDiv') || window;

        if (scrollContainer === window) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        } else {
            scrollContainer.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }, []);

    const addNewChatRecord = (chat, index) => {
        setApplications((prev) => {
            if (prev[index].active_chats.find((c) => c.id === chat.id)) {
                return prev;
            }

            const newApplications = [...prev];
            newApplications[index].active_chats = [
                chat,
                ...newApplications[index].active_chats,
            ];
            return newApplications;
        });
    };

    const rejectApplication = (enquiryId) => {
        setApplications((prev) => {
            return prev.filter((app) => app.enquiry_details.id !== enquiryId);
        });
    };

    return (
        <Box display={'flex'} flexDirection={'column'} height={'100%'} overflow={'auto'}>
            <OverlayLoader open={loading} />
            <Collapse in={isSmallScreen ? !showScrollTop : true}>
                <Box
                    alignItems="center"
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}
                    m={2}
                    mb={1}
                    gap={1}
                >
                    {(isSupport || isPartner || isConnector) && (
                        <FilterBar
                            displayRejectionReason={displayRejectionReasonFilter}
                        />
                    )}
                    {(isSupport && showAllCloudDownload) && (
                        <Button
                            onClick={() => handleDownloadReport('allCloudLeads')}
                            endIcon={<DownloadIcon />}
                            aria-label={`Download CSV Report for ${loanType}`}
                            sx={(theme) => ({
                                px: 3,
                                backgroundColor: theme.palette.primary.main,
                                color: '#fff',
                                '&:hover': { color: '#fff' },
                            })}
                        >
                            AllCloud
                        </Button>
                    )}
                    {!isSmallScreen && (
                        <Box>
                            <ToggleButtonGroup
                                size="small"
                                color="primary"
                                value={currentView}
                                exclusive
                                onChange={handleViewChange}
                                sx={{ display: 'flex', width: 'fit-content' }}
                            >
                                <ToggleButton value="grid" aria-label="Grid View">
                                    <ViewAgendaIcon />
                                </ToggleButton>
                                <ToggleButton value="table" aria-label="Table View">
                                    <TableChartIcon />
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    )}
                    {isSupport && (
                        <Box alignItems="center" sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Tooltip title={`Download CSV Report for ${loanType}`}>
                                <IconButton
                                    onClick={() => handleDownloadReport('enquiryDetails')}
                                    aria-label={`Download CSV Report for ${loanType}`}
                                >
                                    <DownloadIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
            </Collapse>

            <Box px={2} height={'100%'} overflow={'auto'}>
                {currentView === 'grid' ? (
                    <Box
                        id="scrollableDiv"
                        style={{
                            height: '100%',
                            overflow: 'auto',
                        }}
                    >
                        <InfiniteScroll
                            dataLength={applications.length}
                            next={loadMore}
                            scrollThreshold={0.8}
                            hasMore={hasMore}
                            loader={!isInitialLoad && <Box display="flex" justifyContent="center" p={2}><CircularProgress size={30} /></Box>}
                            endMessage={
                                <Box textAlign="center" p={2}>
                                    {applications.length > 0 && (
                                        <Typography variant="body2" color="textSecondary">
                                            No more applications to show
                                        </Typography>
                                    )}
                                </Box>
                            }
                            scrollableTarget="scrollableDiv"
                        >
                            {applications.length > 0 ?
                                applications.map((application, index) => (
                                    <Grid2 key={application.id || index} mt={2} mb={2} xs={12} sm={6} md={4} lg={3}>
                                        <LoanCard
				                            key={application.enquiry_details.id}
                                            title={application?.enquiry_details.business_name
                                                ? `${application.enquiry_details.business_name} (${application.enquiry_details.whatsapp_number})`
                                                : application.enquiry_details.whatsapp_number}
                                            application={application}
                                            allStatus={allStatus ?? (
                                                application.loan_type?.code
                                                    ? resellerStatusPerLoanType[loanType]['all'](application.current_status_code)
                                                    : resellerStatusPerLoanType["null"]?.in_progress
                                                )}
                                            currentStatusCode={currentStatusCode ?? application.current_status_code}
                                            resellerData={resellerData}
                                            addNewChatRecord={(chat) => addNewChatRecord(chat, index)}
                                            rejectApplication={rejectApplication}
                                            loanType = {loanType}
                                            showStepper={showStepper}
                                        />
                                    </Grid2>
                                ))
                                : <p>No Enquiries available</p>
                            }
                        </InfiniteScroll>
                    </Box>
                ) : (
                    <LoanTable
                        applications={applications}
                        allStatus={allStatus}
                        currentStatusCode={currentStatusCode}
                        resellerData={resellerData}
                        loadMore={loadMore}
                        hasMore={hasMore}
                        addNewChatRecord={addNewChatRecord}
                        rejectApplication={rejectApplication}
                    />
                )}

                <Zoom in={showScrollTop}>
                    <Fab
                        color="primary"
                        size="small"
                        aria-label="scroll to top"
                        onClick={scrollToTop}
                        sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 2000 }}
                    >
                        <KeyboardArrowUpIcon />
                    </Fab>
                </Zoom>
            </Box>
        </Box>
    );
}